import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../MasterAdmin/ui/ConfirmDialog';
import {
  ActionMenuItem,
  ActionsDropdownMenu,
} from '../MasterAdmin/deep-links/ActionsDropdownMenu';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import {
  downloadWhatsAppContactTemplateCsv,
  downloadWhatsAppContactTemplateExcel,
} from './downloadWhatsAppContactTemplate';
import {
  parseWhatsAppContactCsvText,
  parseWhatsAppContactFile,
  type WhatsAppContactImportRow,
} from './parseWhatsAppContactFile';
import { normalizeContactPhoneInput, recipientKey } from './whatsappPreview';
import type { WhatsAppContact } from '../../models/MasterAdmin/whatsappHub';
import { WHATSAPP_SEND_PATH } from '../../models/MasterAdmin/whatsappHub';
import {
  createWhatsAppContact,
  deleteWhatsAppContact,
  fetchWhatsAppContacts,
  importWhatsAppContacts,
  updateWhatsAppContact,
} from '../../services/MasterAdmin/whatsappCampaignService';

type ContactFormState = {
  displayName: string;
  phone: string;
  tags: string;
  notes: string;
};

type ContactOverview = {
  totalContacts: number;
  totalTags: number;
  linkedUsers: number;
  linkedVendors: number;
  withMessages: number;
  tags: string[];
};

type ContactPagination = {
  total: number;
  limit: number;
  offset: number;
  page: number;
  totalPages: number;
};

const emptyForm = (): ContactFormState => ({
  displayName: '',
  phone: '',
  tags: '',
  notes: '',
});

const EMPTY_OVERVIEW: ContactOverview = {
  totalContacts: 0,
  totalTags: 0,
  linkedUsers: 0,
  linkedVendors: 0,
  withMessages: 0,
  tags: [],
};

const PAGE_SIZE = 50;
const IMPORT_CHUNK_SIZE = 200;

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

function contactToForm(c: WhatsAppContact): ContactFormState {
  return {
    displayName: c.displayName || '',
    phone: c.phone || `${c.countryCode || '+91'}${c.phoneNumber || ''}`,
    tags: (c.tags || []).join(', '),
    notes: c.notes || '',
  };
}

function parseDefaultTags(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[,|;]/)
        .map((t) => t.trim())
        .filter(Boolean)
    )
  );
}

const WhatsAppContactsPanel: React.FC = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<WhatsAppContact[]>([]);
  const [overview, setOverview] = useState<ContactOverview>(EMPTY_OVERVIEW);
  const [pagination, setPagination] = useState<ContactPagination>({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
    page: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewContact, setViewContact] = useState<WhatsAppContact | null>(null);
  const [editing, setEditing] = useState<WhatsAppContact | null>(null);
  const [form, setForm] = useState<ContactFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppContact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importRows, setImportRows] = useState<WhatsAppContactImportRow[]>([]);
  const [importDefaultTag, setImportDefaultTag] = useState('');
  const [parsingFile, setParsingFile] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState('');
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  const resetImportState = () => {
    setImportText('');
    setImportFile(null);
    setImportRows([]);
    setImportDefaultTag('');
    setImportProgress('');
  };

  const openImportModal = () => {
    resetImportState();
    setImportOpen(true);
  };

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, tagFilter]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const res = await fetchWhatsAppContacts({
        search: debouncedSearch || undefined,
        tag: tagFilter || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setContacts(res.data || []);
      setOverview(res.overview || EMPTY_OVERVIEW);
      setPagination({
        total: res.pagination?.total || 0,
        limit: res.pagination?.limit || PAGE_SIZE,
        offset: res.pagination?.offset || offset,
        page: res.pagination?.page || page,
        totalPages: res.pagination?.totalPages || 1,
      });
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, tagFilter, page]);

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  const tagOptions = useMemo(() => overview.tags || [], [overview.tags]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (c: WhatsAppContact) => {
    setEditing(c);
    setForm(contactToForm(c));
    setModalOpen(true);
    setViewContact(null);
    setOpenMenuKey(null);
  };

  const handleSave = async () => {
    const normalized = normalizeContactPhoneInput(form.phone);
    if (!normalized) {
      toast.error('Enter a valid 10-digit mobile or full number (e.g. +919876543210)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        displayName: form.displayName.trim() || undefined,
        countryCode: normalized.countryCode,
        phoneNumber: normalized.phoneNumber,
        tags: parseDefaultTags(form.tags),
        notes: form.notes.trim() || undefined,
      };
      if (editing) {
        await updateWhatsAppContact(editing.id, payload);
        toast.success('Contact updated');
      } else {
        await createWhatsAppContact(payload);
        toast.success('Contact added');
      }
      setModalOpen(false);
      await loadContacts();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteWhatsAppContact(deleteTarget.id);
      toast.success('Contact deleted');
      setDeleteTarget(null);
      if (viewContact?.id === deleteTarget.id) setViewContact(null);
      await loadContacts();
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleImportFile = async (file: File | null) => {
    if (!file) return;
    setParsingFile(true);
    setImportFile(file);
    setImportText('');
    try {
      const rows = await parseWhatsAppContactFile(file);
      setImportRows(rows);
      toast.success(`${rows.length} contact row${rows.length === 1 ? '' : 's'} ready to import`);
    } catch (e: unknown) {
      setImportFile(null);
      setImportRows([]);
      toast.error(e instanceof Error ? e.message : 'Could not read file');
    } finally {
      setParsingFile(false);
    }
  };

  const handleImportTextChange = (text: string) => {
    setImportText(text);
    setImportFile(null);
    setImportRows(parseWhatsAppContactCsvText(text));
  };

  const handleImport = async () => {
    const rows = importRows.length ? importRows : parseWhatsAppContactCsvText(importText);
    if (!rows.length) {
      toast.error(
        'Upload a CSV/Excel file or paste CSV with header row: name, phone, country_code, tags, notes'
      );
      return;
    }
    const defaultTags = parseDefaultTags(importDefaultTag);
    setImporting(true);
    let created = 0;
    let updated = 0;
    let skipped = 0;
    try {
      const totalChunks = Math.ceil(rows.length / IMPORT_CHUNK_SIZE);
      for (let i = 0; i < rows.length; i += IMPORT_CHUNK_SIZE) {
        const chunkIndex = Math.floor(i / IMPORT_CHUNK_SIZE) + 1;
        setImportProgress(`Importing chunk ${chunkIndex}/${totalChunks}…`);
        const chunk = rows.slice(i, i + IMPORT_CHUNK_SIZE);
        const res = await importWhatsAppContacts(chunk, { defaultTags });
        const d = res.data as { created: number; updated: number; skipped: number };
        created += d.created || 0;
        updated += d.updated || 0;
        skipped += d.skipped || 0;
      }
      toast.success(`Import done: ${created} added, ${updated} updated, ${skipped} skipped`);
      setImportOpen(false);
      resetImportState();
      setPage(1);
      await loadContacts();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
      setImportProgress('');
    }
  };

  const openSend = (c: WhatsAppContact) => {
    setOpenMenuKey(null);
    navigate(WHATSAPP_SEND_PATH, {
      state: {
        recipients: [
          {
            key: recipientKey(c.countryCode, c.phoneNumber),
            countryCode: c.countryCode,
            phoneNumber: c.phoneNumber,
            displayName: c.displayName,
            maskedPhone: c.maskedPhone,
            source: 'contact',
            tags: c.tags,
          },
        ],
      },
    });
  };

  const overviewCards = [
    { label: 'Total contacts', value: overview.totalContacts },
    { label: 'Total tags', value: overview.totalTags },
    { label: 'Linked users', value: overview.linkedUsers },
    { label: 'Linked vendors', value: overview.linkedVendors },
    { label: 'With messages', value: overview.withMessages },
  ];

  return (
    <>
      <WhatsAppPanelShell
        title="Contacts"
        description="Add, import, and manage WhatsApp contacts. Use them when sending template messages."
        actions={
          <>
            <button
              type="button"
              onClick={openImportModal}
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm dark:border-gray-600"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Import
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-semibold text-white"
            >
              <PlusIcon className="h-4 w-4" />
              Add contact
            </button>
          </>
        }
      >
        <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {overviewCards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
              <p className="mt-1 text-xl font-bold text-gray-900 dark:text-white">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, phone, or notes..."
              className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-900"
            />
          </div>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 sm:w-56"
          >
            <option value="">All tags</option>
            {tagOptions.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading contacts...</p>
        ) : contacts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-600">
            <p className="text-sm text-gray-500">No contacts match your filters.</p>
            <p className="mt-2 text-xs text-gray-400">
              Download the template from Import — columns: name, phone, country_code, tags, notes
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Tags</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                        {c.displayName}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{c.maskedPhone}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {(c.tags || []).length ? (
                            (c.tags || []).slice(0, 4).map((tag) => (
                              <button
                                key={tag}
                                type="button"
                                onClick={() => setTagFilter(tag)}
                                className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] hover:bg-[#25D366]/15 dark:bg-gray-700"
                              >
                                {tag}
                              </button>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                          {(c.tags || []).length > 4 ? (
                            <span className="text-[10px] text-gray-400">+{(c.tags || []).length - 4}</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <ActionsDropdownMenu
                            open={openMenuKey === c.id}
                            onToggle={() =>
                              setOpenMenuKey((prev) => (prev === c.id ? null : c.id))
                            }
                            onClose={() => setOpenMenuKey(null)}
                          >
                            <ActionMenuItem
                              icon={<PaperAirplaneIcon className="h-4 w-4" />}
                              label="Send message"
                              onClick={() => openSend(c)}
                            />
                            <ActionMenuItem
                              icon={<Eye className="h-4 w-4" />}
                              label="View"
                              onClick={() => {
                                setViewContact(c);
                                setOpenMenuKey(null);
                              }}
                            />
                            <ActionMenuItem
                              icon={<PencilIcon className="h-4 w-4" />}
                              label="Edit"
                              onClick={() => openEdit(c)}
                            />
                            <ActionMenuItem
                              icon={<TrashIcon className="h-4 w-4" />}
                              label="Delete"
                              tone="danger"
                              onClick={() => {
                                setDeleteTarget(c);
                                setOpenMenuKey(null);
                              }}
                            />
                          </ActionsDropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
              <p className="text-gray-500">
                Showing {pagination.offset + 1}–
                {Math.min(pagination.offset + contacts.length, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border px-3 py-1.5 disabled:opacity-50 dark:border-gray-600"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500">
                  Page {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= pagination.totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border px-3 py-1.5 disabled:opacity-50 dark:border-gray-600"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </WhatsAppPanelShell>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editing ? 'Edit contact' : 'Add contact'}</h3>
              <button type="button" onClick={() => setModalOpen(false)}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-3">
              <input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Display name"
                className={inputClass}
              />
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone (10-digit or +91XXXXXXXXXX)"
                className={inputClass}
              />
              <p className="text-[11px] text-gray-400">Example: 9370320066 or +919370320066</p>
              <input
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="Tags (comma separated)"
                className={inputClass}
              />
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Notes"
                rows={3}
                className={inputClass}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewContact && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">{viewContact.displayName}</h3>
              <button type="button" onClick={() => setViewContact(null)}>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Phone</dt>
                <dd>{viewContact.maskedPhone}</dd>
              </div>
              {viewContact.email ? (
                <div>
                  <dt className="text-xs text-gray-500">Email</dt>
                  <dd>{viewContact.email}</dd>
                </div>
              ) : null}
              {viewContact.city ? (
                <div>
                  <dt className="text-xs text-gray-500">City</dt>
                  <dd>{viewContact.city}</dd>
                </div>
              ) : null}
              <div>
                <dt className="text-xs text-gray-500">Tags</dt>
                <dd>{(viewContact.tags || []).join(', ') || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Notes</dt>
                <dd className="whitespace-pre-wrap">{viewContact.notes || '—'}</dd>
              </div>
            </dl>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => openEdit(viewContact)}
                className="rounded-lg border px-4 py-2 text-sm"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  openSend(viewContact);
                  setViewContact(null);
                }}
                className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white"
              >
                Send message
              </button>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Import contacts</h3>
              <button
                type="button"
                onClick={() => {
                  setImportOpen(false);
                  resetImportState();
                }}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
              <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">Download template</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={downloadWhatsAppContactTemplateExcel}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-gray-800"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  Excel (.xlsx)
                </button>
                <button
                  type="button"
                  onClick={downloadWhatsAppContactTemplateCsv}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:hover:bg-gray-800"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  CSV (.csv)
                </button>
              </div>
            </div>

            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              Default tag (optional)
            </label>
            <input
              value={importDefaultTag}
              onChange={(e) => setImportDefaultTag(e.target.value)}
              placeholder="e.g. june-campaign — applied when row has no tag"
              className={`${inputClass} mb-3`}
              disabled={parsingFile || importing}
            />
            <p className="mb-3 text-[11px] text-gray-500">
              If the Excel/CSV row has no tag, this tag is added. Large imports are sent in chunks of{' '}
              {IMPORT_CHUNK_SIZE}.
            </p>

            <label className="mb-3 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 px-4 py-6 text-center hover:border-[#25D366] hover:bg-[#25D366]/5 dark:border-gray-600">
              <ArrowUpTrayIcon className="mb-2 h-8 w-8 text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {parsingFile ? 'Reading file...' : 'Upload CSV or Excel file'}
              </span>
              <span className="mt-1 text-xs text-gray-500">.csv, .xlsx, .xls</span>
              {importFile && !parsingFile ? (
                <span className="mt-2 text-xs font-medium text-[#128C7E]">
                  {importFile.name} · {importRows.length} row{importRows.length === 1 ? '' : 's'}
                </span>
              ) : null}
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                disabled={parsingFile || importing}
                onChange={(e) => void handleImportFile(e.target.files?.[0] || null)}
              />
            </label>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-400 dark:bg-gray-900">or paste CSV</span>
              </div>
            </div>

            <p className="mb-2 text-xs text-gray-500">
              Headers: name, phone, country_code (optional), tags, notes
            </p>
            <textarea
              value={importText}
              onChange={(e) => handleImportTextChange(e.target.value)}
              rows={5}
              placeholder={'name,phone,country_code,tags,notes\nJohn Doe,9876543210,+91,vip,Follow up\n'}
              className={inputClass}
              disabled={parsingFile || importing}
            />
            {!importFile && importRows.length > 0 ? (
              <p className="mt-1 text-xs text-[#128C7E]">{importRows.length} row(s) ready to import</p>
            ) : null}
            {importProgress ? <p className="mt-2 text-xs text-gray-500">{importProgress}</p> : null}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setImportOpen(false);
                  resetImportState();
                }}
                className="rounded-lg border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={importing || parsingFile || (!importRows.length && !importText.trim())}
                onClick={() => void handleImport()}
                className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {importing
                  ? 'Importing...'
                  : `Import${importRows.length ? ` (${importRows.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete contact"
        message={`Remove ${deleteTarget?.displayName}? Inbox message history is kept.`}
        tone="danger"
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        busy={deleting}
        onCancel={() => !deleting && setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
      />
    </>
  );
};

export default WhatsAppContactsPanel;
